'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient, type UserBet } from '@/lib/api';
import {
  categoryLabel,
  formatDateTime,
  formatRelative,
  formatTimeUntil,
  formatUsdt,
  liveEstimateNetWinningsMicro,
} from '@/lib/format';
import { getInitData, hapticImpact } from '@/lib/tma';

type Tab = 'active' | 'history';

interface Aggregate {
  bets_count: number;
  active_count: number;
  won_count: number;
  lost_count: number;
  refunded_count: number;
  total_at_stake_micro: number;
  total_volume_micro: number;
  total_payouts_micro: number;
}

export function MyBets({ refreshKey }: { refreshKey: number }) {
  const [bets, setBets] = useState<UserBet[] | null>(null);
  const [aggregate, setAggregate] = useState<Aggregate | null>(null);
  const [tab, setTab] = useState<Tab>('active');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const api = createApiClient(getInitData);
    api
      .myBets(50)
      .then((r) => {
        setBets(r.bets);
        setAggregate(r.aggregate);
      })
      .catch((err) => {
        const e = err as ApiClientError;
        setError(e?.message ?? 'Не удалось загрузить ставки');
      });
  }, [refreshKey]);

  if (error) {
    return (
      <div className="bg-no/10 border-l-[3px] border-no p-4 mb-4 text-no text-sm">⚠️ {error}</div>
    );
  }

  if (!bets) {
    return (
      <div className="bg-bg-elev p-6 mb-4 flex justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (bets.length === 0) {
    return (
      <div className="bg-bg-elev p-5 mb-4 text-center border-l-[3px] border-line">
        <p className="display text-[14px] mb-1">СТАВОК ЕЩЁ НЕТ</p>
        <p className="font-mono text-[10px] text-text-tertiary tracking-[0.1em]">
          ОТКРОЙ РЫНКИ И ВЫБЕРИ ПЕРВЫЙ ПРОГНОЗ
        </p>
      </div>
    );
  }

  const active = bets.filter((b) => b.status === 'active');
  const history = bets.filter((b) => b.status !== 'active');
  const visible = tab === 'active' ? active : history;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="display text-[14px] tracking-[0.05em]">МОИ СТАВКИ</h2>
        {aggregate && aggregate.total_at_stake_micro > 0 && (
          <span className="font-mono text-[10px] text-text-tertiary tracking-[0.1em] tabular-nums">
            В ИГРЕ:{' '}
            <span className="text-accent">{formatUsdt(aggregate.total_at_stake_micro)}</span>
          </span>
        )}
      </div>

      {/* Tabs (sharp V2) */}
      <div className="grid grid-cols-2 gap-1 mb-3">
        <TabButton
          active={tab === 'active'}
          onClick={() => {
            hapticImpact('light');
            setTab('active');
          }}
        >
          АКТИВНЫЕ {active.length > 0 && <span className="ml-1 text-text-tertiary">({active.length})</span>}
        </TabButton>
        <TabButton
          active={tab === 'history'}
          onClick={() => {
            hapticImpact('light');
            setTab('history');
          }}
        >
          ИСТОРИЯ {history.length > 0 && <span className="ml-1 text-text-tertiary">({history.length})</span>}
        </TabButton>
      </div>

      {visible.length === 0 ? (
        <div className="bg-bg-elev p-5 text-center font-mono text-[11px] text-text-tertiary tracking-[0.1em]">
          {tab === 'active' ? 'НЕТ АКТИВНЫХ СТАВОК' : 'ИСТОРИЯ ПУСТА'}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((b) => (
            <BetRow key={b.prediction_id} bet={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`py-2.5 display text-[11px] tracking-[0.08em] transition-colors ${
        active ? 'bg-accent text-bg-base' : 'bg-bg-elev text-text-secondary'
      }`}
    >
      {children}
    </button>
  );
}

function BetRow({ bet }: { bet: UserBet }) {
  const isYes = bet.side === 1;
  const sideLabel = isYes ? 'ДА' : 'НЕТ';
  const sideClasses = isYes
    ? 'bg-yes/15 text-yes border border-yes/30'
    : 'bg-no/15 text-no border border-no/30';
  const borderColor = isYes ? 'border-yes' : 'border-no';

  const statusBadge = renderStatusBadge(bet);

  return (
    <Link
      href={`/market?id=${encodeURIComponent(bet.market_id)}`}
      onClick={() => hapticImpact('light')}
      className={`block bg-bg-elev p-4 transition-colors active:scale-[0.99] border-l-[3px] ${borderColor}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-1">
            {categoryLabel(bet.market_category).toUpperCase()}
          </p>
          <p className="text-[13px] leading-snug font-medium line-clamp-2">
            {bet.market_question}
          </p>
        </div>
        <span
          className={`px-2 py-0.5 ${sideClasses} display text-[10px] tracking-[0.08em] flex-shrink-0`}
        >
          {sideLabel}
        </span>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="display text-[14px] tabular-nums">
            {formatUsdt(bet.amount_micro)}
          </span>
          <span className="text-text-tertiary">·</span>
          <time
            title={formatDateTime(bet.created_at)}
            className="font-mono text-[10px] text-text-tertiary tracking-[0.05em]"
          >
            {formatRelative(bet.created_at)}
          </time>
        </div>
        {statusBadge}
      </div>

      <BetDetails bet={bet} />
    </Link>
  );
}

function BetDetails({ bet }: { bet: UserBet }) {
  if (bet.status === 'won') {
    const profit = (bet.payout_micro ?? 0) - bet.amount_micro;
    return (
      <div className="flex items-center justify-between text-[11px] pt-2 border-t border-line">
        <span className="font-mono text-text-tertiary tracking-[0.1em]">ВЫПЛАТА</span>
        <span className="text-yes tabular-nums font-bold">
          {formatUsdt(bet.payout_micro ?? 0)}{' '}
          <span className="text-text-tertiary font-normal">(+{formatUsdt(profit)})</span>
        </span>
      </div>
    );
  }

  if (bet.status === 'lost') {
    return (
      <div className="flex items-center justify-between text-[11px] pt-2 border-t border-line">
        <span className="font-mono text-text-tertiary tracking-[0.1em]">РЕЗУЛЬТАТ</span>
        <span className="text-no tabular-nums font-bold">−{formatUsdt(bet.amount_micro)}</span>
      </div>
    );
  }

  if (bet.status === 'refunded') {
    return (
      <div className="flex items-center justify-between text-[11px] pt-2 border-t border-line">
        <span className="font-mono text-text-tertiary tracking-[0.1em]">ВОЗВРАТ</span>
        <span className="text-text-secondary tabular-nums">
          {formatUsdt(bet.payout_micro ?? bet.amount_micro)}
        </span>
      </div>
    );
  }

  // Active: live estimate
  const liveWinnings = liveEstimateNetWinningsMicro(
    bet.amount_micro,
    bet.side,
    bet.pool_yes_at_bet_micro,
    bet.pool_no_at_bet_micro,
    bet.current_pool_yes_micro,
    bet.current_pool_no_micro,
    bet.market_fee_bps,
  );
  const livePayout = bet.amount_micro + liveWinnings;
  const roiPct = bet.amount_micro > 0 ? (liveWinnings / bet.amount_micro) * 100 : 0;
  const sideLabel = bet.side === 1 ? 'ДА' : 'НЕТ';
  const sideColor = bet.side === 1 ? 'text-yes' : 'text-no';

  return (
    <div className="pt-2 border-t border-line text-[11px]">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-text-tertiary tracking-[0.05em]">
          Заработаешь, если резолвят как{' '}
          <span className={`${sideColor} font-bold`}>{sideLabel}</span>
        </span>
        <span className="text-yes tabular-nums font-bold">
          +{formatUsdt(liveWinnings)} ({roiPct >= 0 ? '+' : ''}
          {roiPct.toFixed(0)}%)
        </span>
      </div>
      <div className="flex items-center justify-between font-mono text-[10px] text-text-tertiary tracking-[0.05em]">
        <span>получишь {formatUsdt(livePayout)}</span>
        <span>до закрытия {formatTimeUntil(bet.market_close_at)}</span>
      </div>
    </div>
  );
}

function renderStatusBadge(bet: UserBet) {
  const cls = 'px-2 py-0.5 display text-[9px] tracking-[0.1em]';
  switch (bet.status) {
    case 'active':
      return <span className={`${cls} bg-bg-elev2 text-text-secondary`}>В ИГРЕ</span>;
    case 'won':
      return <span className={`${cls} bg-yes/20 text-yes`}>ВЫИГРАНО</span>;
    case 'lost':
      return <span className={`${cls} bg-no/20 text-no`}>ПРОИГРАНО</span>;
    case 'refunded':
      return <span className={`${cls} bg-text-tertiary/20 text-text-secondary`}>ВОЗВРАТ</span>;
    default:
      return null;
  }
}
